<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContactsAPI from 'dashboard/api/contacts';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();

const isLoading = ref(true);
const members = ref([]);
const query = ref('');

const filteredMembers = computed(() => {
  const q = query.value.toLowerCase().trim();
  if (!q) return members.value;
  return members.value.filter(m => {
    const haystack = `${m.name || ''} ${m.username || ''}`.toLowerCase();
    return haystack.includes(q);
  });
});

const fetchMembers = async () => {
  isLoading.value = true;
  try {
    const { data } = await ContactsAPI.getGroupMembers(route.params.contactId);
    members.value = data.members || [];
  } catch {
    members.value = [];
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.GROUP_MEMBERS.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchMembers);
watch(() => route.params.contactId, fetchMembers);
</script>

<template>
  <div
    v-if="isLoading"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div v-else class="px-6 py-4 flex flex-col gap-3">
    <input
      v-model="query"
      type="text"
      :placeholder="t('CONTACTS_LAYOUT.SIDEBAR.GROUP_MEMBERS.SEARCH')"
      class="w-full border border-n-strong rounded px-2 py-1 text-sm bg-n-alpha-black2 text-n-slate-12"
    />
    <p v-if="filteredMembers.length === 0" class="py-6 text-sm text-center text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.SIDEBAR.GROUP_MEMBERS.EMPTY') }}
    </p>
    <ul v-else class="flex flex-col gap-1">
      <li
        v-for="m in filteredMembers"
        :key="m.id"
        class="flex items-center gap-2 py-1 border-b border-n-strong last:border-b-0"
      >
        <span class="flex-1 text-sm text-n-slate-12 truncate">
          {{ m.name }}
          <span v-if="m.username" class="text-n-slate-10">@{{ m.username }}</span>
        </span>
        <span
          v-if="m.is_support"
          class="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-n-teal-4 text-n-teal-12"
        >
          {{ t('CONTACTS_LAYOUT.SIDEBAR.GROUP_MEMBERS.SUPPORT_BADGE') }}
        </span>
      </li>
    </ul>
  </div>
</template>
